"use client";

import { useState, useCallback } from "react";
import { ChevronDown, HelpCircle } from "lucide-react";

interface FaqItem {
  id: string;
  q: string;
  a: string;
}

interface FaqAccordionProps {
  items: FaqItem[];
  title?: string;
  badge?: string;
}

/**
 * Client island for FAQ accordion expand/collapse.
 * All FAQ text is passed as props from a server component,
 * so the content IS in the initial HTML for SEO crawling.
 */
export default function FaqAccordion({
  items,
  title = "Technical FAQ",
  badge = "Support",
}: FaqAccordionProps) {
  const [expanded, setExpanded] = useState<string | null>(null);

  const toggle = useCallback((id: string) => {
    setExpanded((prev) => (prev === id ? null : id));
  }, []);

  return (
    <section className="py-12 sm:py-16 lg:py-20 bg-slate-50 dark:bg-slate-800/50 border-t border-slate-100 dark:border-slate-800">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8 sm:mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#d47723]/10 text-[#d47723] font-semibold text-xs tracking-wide uppercase mb-4">
            <HelpCircle className="w-3.5 h-3.5" />
            {badge}
          </div>
          <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 dark:text-white mb-3">
            {title}
          </h2>
        </div>
        <div className="space-y-3">
          {items.map((faq) => (
            <div
              key={faq.id}
              className="bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden"
            >
              <button
                type="button"
                className="w-full flex items-center justify-between gap-3 px-4 py-3.5 sm:px-5 sm:py-4 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-[#d47723] focus-visible:ring-offset-2 dark:focus-visible:ring-offset-slate-900 transition-colors"
                onClick={() => toggle(faq.id)}
                aria-expanded={expanded === faq.id}
              >
                <span className="text-sm font-semibold text-slate-800 dark:text-white leading-snug pr-2">
                  {faq.q}
                </span>
                <ChevronDown
                  className={`w-4 h-4 text-slate-400 dark:text-slate-500 flex-shrink-0 transition-transform duration-300 ${
                    expanded === faq.id ? "rotate-180" : ""
                  }`}
                />
              </button>
              {/* Answer text is always in the DOM for SEO — just visually hidden */}
              <div
                className={`overflow-hidden transition-all duration-300 ease-in-out ${
                  expanded === faq.id
                    ? "max-h-96 opacity-100"
                    : "max-h-0 opacity-0"
                }`}
              >
                <div className="px-4 pb-4 sm:px-5 sm:pb-5 text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
                  {faq.a}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
